/**
 * Document Note Template Utilities
 * Provides reusable functions for document note template selection and management
 */

/**
 * Backend enum values for DocumentType
 * IMPORTANT: These values must match the backend enum exactly
 */
export const DocumentType = {
  SaleTicket: 0,
  RepairTicket: 1,
  Invoice: 2,
  DeliveryNote: 3,
  Offer: 4,
  CreditNote: 5,
  All: 99
};

/**
 * Get display label for document type
 */
export const getDocumentTypeLabel = (type) => {
  const labels = {
    [DocumentType.SaleTicket]: 'Ticket de Caisse',
    [DocumentType.RepairTicket]: 'Ticket de Caisse Réparation',
    [DocumentType.Invoice]: 'Invoice',
    [DocumentType.DeliveryNote]: 'Delivery Note',
    [DocumentType.Offer]: 'Offer',
    [DocumentType.CreditNote]: 'Credit Note',
    [DocumentType.All]: 'All'
  };
  return labels[type] || 'Unknown';
};

/**
 * Get color for document type chip
 */
export const getDocumentTypeColor = (type) => {
  const colors = {
    [DocumentType.SaleTicket]: 'primary',
    [DocumentType.RepairTicket]: 'secondary',
    [DocumentType.Invoice]: 'success',
    [DocumentType.DeliveryNote]: 'warning',
    [DocumentType.Offer]: 'info',
    [DocumentType.CreditNote]: 'error',
    [DocumentType.All]: 'default'
  };
  return colors[type] || 'default';
};

/**
 * Find the first matching template for a specific document type
 * Falls back to "All" type if no specific template found
 * 
 * @param {Array} templates - Array of template objects
 * @param {number} documentType - DocumentType enum value
 * @returns {Object|null} - First matching template or null
 */
export const findDefaultTemplate = (templates, documentType) => {
  if (!templates || templates.length === 0) return null;
  
  // First try to find template for specific document type
  const specificTemplate = templates.find(t => t.DocumentType === documentType && t.IsDefault);
  if (specificTemplate) return specificTemplate;
  
  // Try to find any template for specific type (not just default)
  const anySpecificTemplate = templates.find(t => t.DocumentType === documentType);
  if (anySpecificTemplate) return anySpecificTemplate;
  
  // Fall back to "All" type template (default first)
  const allTemplateDefault = templates.find(t => t.DocumentType === DocumentType.All && t.IsDefault);
  if (allTemplateDefault) return allTemplateDefault;
  
  // Fall back to any "All" type template
  const anyAllTemplate = templates.find(t => t.DocumentType === DocumentType.All);
  if (anyAllTemplate) return anyAllTemplate;
  
  return null;
};

/**
 * Filter templates by document type (includes "All" type)
 * 
 * @param {Array} templates - Array of template objects
 * @param {number} documentType - DocumentType enum value
 * @returns {Array} - Filtered templates
 */
export const filterTemplatesByType = (templates, documentType) => {
  if (!templates || templates.length === 0) return [];
  
  return templates.filter(t => 
    t.DocumentType === documentType || t.DocumentType === DocumentType.All
  );
};

/**
 * Get the template ID for auto-selection
 * Returns empty string if no suitable template found
 * 
 * @param {Array} templates - Array of template objects
 * @param {number} documentType - DocumentType enum value
 * @returns {string} - Template ID or empty string
 */
export const getDefaultTemplateId = (templates, documentType) => {
  const template = findDefaultTemplate(templates, documentType);
  return template ? String(template.Id) : '';
};

/**
 * Get the note content from a template
 * 
 * @param {Array} templates - Array of template objects
 * @param {string|number} templateId - Template ID to find
 * @returns {string} - Note content or empty string
 */
export const getTemplateContent = (templates, templateId) => {
  if (!templates || !templateId) return '';
  
  const template = templates.find(t => String(t.Id) === String(templateId));
  return template ? template.NoteContent : '';
};

/**
 * Sort templates by priority: defaults first, then by name
 * 
 * @param {Array} templates - Array of template objects
 * @returns {Array} - Sorted templates
 */
export const sortTemplates = (templates) => {
  if (!templates || templates.length === 0) return [];
  
  return [...templates].sort((a, b) => {
    // Defaults first
    if (a.IsDefault && !b.IsDefault) return -1;
    if (!a.IsDefault && b.IsDefault) return 1;
    
    // Then alphabetically by name
    return (a.TemplateName || '').localeCompare(b.TemplateName || '');
  });
};
